import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { fetchRecipe } from "../lib/api";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import JsonLd from "../components/JsonLd";
import SaveRecipeButton from "../components/SaveRecipeButton";
import useSeo from "../lib/useSeo";
import {
  Clock,
  Users,
  Baby,
  ArrowLeft,
  ChefHat,
  Sparkles,
  Heart,
  Utensils,
  Loader2,
  CheckCircle2,
} from "lucide-react";

export default function RecipeDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [done, setDone] = useState([]);

  useEffect(() => {
    setLoading(true);
    fetchRecipe(id)
      .then((d) => setData(d))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
    window.scrollTo(0, 0);
  }, [id]);

  const toggleStep = (i) =>
    setDone((d) => (d.includes(i) ? d.filter((x) => x !== i) : [...d, i]));

  const preRecipe = data?.recipe;

  // Per-page SEO (Google indexes each recipe with its own title/description/image).
  // Called unconditionally so hook order is stable across loading/error states.
  useSeo({
    title: preRecipe
      ? `${preRecipe.title} — kid-friendly recipe | Plate Buddies`
      : "Recipe | Plate Buddies",
    description: preRecipe
      ? (preRecipe.story
          ? `${preRecipe.story} A ${preRecipe.time || "kid-friendly"} family recipe from Plate Buddies.`
          : `Cook ${preRecipe.title} — a ${preRecipe.time || "quick"} kid-friendly family recipe from Plate Buddies.`)
      : "A kid-friendly family recipe from Plate Buddies.",
    image: preRecipe?.image,
    url: preRecipe ? `https://platebuddies.com/recipes/${preRecipe.id}` : undefined,
    type: "article",
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-pb-cream flex items-center justify-center">
        <Loader2 className="h-10 w-10 text-pb-coral animate-spin" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-pb-cream font-body">
        <Navbar />
        <div className="max-w-3xl mx-auto px-6 py-20 text-center">
          <h1 className="font-display font-black text-4xl">Recipe not found</h1>
          <p className="text-pb-soft mt-3">That adventure may have wandered off.</p>
          <Link
            to="/home"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-pb-coral text-white font-bold px-6 py-3"
          >
            <ArrowLeft className="h-4 w-4" /> Back home
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const { recipe, buddy } = data;

  // Build rich-result Recipe schema. Ingredients/instructions come from
  // whatever shape the backend returns — we defensively cast to strings so
  // Google's validator doesn't choke on nested objects.
  const asStrings = (arr) =>
    Array.isArray(arr)
      ? arr.map((it) =>
          typeof it === "string" ? it : it && (it.text || it.name || it.instruction || "")
        ).filter(Boolean)
      : [];
  const recipeSchema = {
    "@context": "https://schema.org",
    "@type": "Recipe",
    name: recipe.title,
    description: recipe.story || `A kid-friendly recipe with ${buddy?.name || "Plate Buddies"}.`,
    image: recipe.image ? [recipe.image] : ["https://platebuddies.com/brand/og-share-v3.jpg"],
    author: { "@type": "Organization", "name": "Plate Buddies" },
    recipeCategory: "Kid-friendly dinner",
    suitableForDiet: [],
    keywords: `kid friendly recipe, family dinner, ${buddy?.name || ""}`.trim().replace(/,\s*$/, ""),
    recipeYield: recipe.serves || "Family of 4",
    totalTime: recipe.time || undefined,
    recipeIngredient: asStrings(recipe.ingredients),
    recipeInstructions: asStrings(recipe.steps || recipe.instructions).map((text) => ({
      "@type": "HowToStep",
      text,
    })),
  };

  return (
    <div data-testid="page-recipe" className="font-body bg-pb-cream min-h-screen">
      <JsonLd id={`recipe-${recipe?.id || "current"}`} data={recipeSchema} />
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div
          className="blob"
          style={{ background: buddy?.color || "#FFD54F", width: 480, height: 480, top: -120, right: -120, opacity: 0.35 }}
        />
        <div className="blob bg-pb-sky" style={{ width: 360, height: 360, top: 60, left: -100, opacity: 0.45 }} />
        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 pt-10 pb-16 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 animate-fade-up">
            <Link
              to="/#recipes"
              data-testid="link-back-recipes"
              className="inline-flex items-center gap-2 text-sm font-bold text-pb-soft hover:text-pb-coral transition"
            >
              <ArrowLeft className="h-4 w-4" /> All recipes
            </Link>
            {buddy && (
              <div className="mt-6 inline-flex items-center gap-3 bg-white rounded-full pl-1 pr-5 py-1.5 shadow-[0_10px_24px_rgba(28,22,40,0.1)] border border-white">
                <span className="h-12 w-12 rounded-full overflow-hidden bg-white border-2 border-white">
                  <img src={buddy.image} alt={buddy.name} className="w-full h-full object-cover" />
                </span>
                <span className="font-extrabold text-pb-ink">
                  with {buddy.name}
                </span>
              </div>
            )}
            <h1 data-testid="text-recipe-title" className="font-display font-black tracking-tight text-pb-ink mt-5 text-5xl md:text-6xl leading-[0.95]">
              {recipe.title}
            </h1>
            <p className="mt-5 text-lg text-pb-soft leading-relaxed max-w-xl">{recipe.story}</p>

            <div className="mt-7 flex flex-wrap gap-3">
              <Meta icon={Clock} label={recipe.time} />
              <Meta icon={Users} label={recipe.serves || "Family of 4"} />
              <Meta icon={Baby} label={recipe.kid_age || "Ages 2+"} />
              <Meta icon={Sparkles} label={recipe.vibe} />
            </div>
          </div>

          <div className="lg:col-span-6 relative">
            <div className="rounded-[2.5rem] overflow-hidden border-[8px] border-white shadow-[0_30px_80px_rgba(28,22,40,0.18)] rotate-[-1deg]">
              <img src={recipe.image} alt={recipe.title} className="w-full h-[420px] md:h-[520px] object-cover" />
            </div>
            {buddy && (
              <div className="absolute -bottom-6 -left-6 bg-white rounded-3xl p-4 shadow-[0_18px_40px_rgba(28,22,40,0.18)] rotate-[3deg] flex items-center gap-3">
                <div
                  className="h-14 w-14 rounded-full overflow-hidden bg-white border-2 border-white"
                >
                  <img src={buddy.image} alt={buddy.name} className="w-full h-full object-cover" />
                </div>
                <div>
                  <p className="text-xs font-extrabold uppercase tracking-widest text-pb-coral">
                    Tonight's Buddy
                  </p>
                  <p className="font-display font-black text-lg">{buddy.name}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-24 grid lg:grid-cols-12 gap-10">
        {/* Ingredients */}
        <aside className="lg:col-span-4">
          <div className="sticky top-24 bg-white rounded-[2rem] p-7 shadow-[0_18px_40px_rgba(28,22,40,0.06)]">
            <h2 className="font-display font-black text-2xl flex items-center gap-2">
              <Utensils className="h-5 w-5 text-pb-coral" /> Ingredients
            </h2>
            <ul data-testid="list-ingredients" className="mt-5 space-y-3">
              {recipe.ingredients.map((ing, i) => (
                <li key={i} className="flex items-start gap-3 text-pb-ink">
                  <span className="h-5 w-5 mt-0.5 rounded-md bg-pb-sun/40 border border-pb-sun flex-shrink-0" />
                  <span className="leading-snug">{ing}</span>
                </li>
              ))}
            </ul>
            <div className="mt-7 rounded-2xl bg-pb-cream p-5 border border-pb-sky/40">
              <p className="text-xs font-extrabold uppercase tracking-widest text-pb-coral">Serves</p>
              <p className="font-display font-black text-xl mt-1">{recipe.serves || "Family of 4"}</p>
            </div>

            {/* Save-recipe CTA */}
            <div className="mt-5 rounded-2xl bg-gradient-to-br from-pb-sun/30 to-pb-coral/10 p-5 border border-pb-sun/50">
              <p className="text-xs font-extrabold uppercase tracking-widest text-pb-coral">Grocery run?</p>
              <p className="font-display font-black text-lg mt-1 leading-tight">
                Send this to your inbox.
              </p>
              <p className="text-xs text-pb-soft mt-1 leading-snug">
                Ingredients + steps, ready for the store or the fridge.
              </p>
              <div className="mt-3">
                <SaveRecipeButton recipe={recipe} variant="pill" className="w-full justify-center" />
              </div>
            </div>
          </div>
        </aside>

        {/* Steps */}
        <div className="lg:col-span-8">
          <div className="bg-white rounded-[2rem] p-8 shadow-[0_18px_40px_rgba(28,22,40,0.06)]">
            <h2 className="font-display font-black text-3xl flex items-center gap-2">
              <ChefHat className="h-6 w-6 text-pb-coral" /> Cook the adventure
            </h2>
            <p className="text-pb-soft mt-2">Tap each step as you finish — kids love crossing things off.</p>
            <ol data-testid="list-steps" className="mt-6 space-y-4">
              {recipe.steps.map((s, i) => {
                const isDone = done.includes(i);
                return (
                  <li key={i}>
                    <button
                      data-testid={`step-${i}`}
                      onClick={() => toggleStep(i)}
                      className={`w-full text-left flex gap-4 items-start p-5 rounded-2xl border-2 transition-all ${
                        isDone
                          ? "bg-pb-green/15 border-pb-green/40"
                          : "bg-pb-cream border-transparent hover:border-pb-coral/30"
                      }`}
                    >
                      <span
                        className={`h-10 w-10 rounded-full flex items-center justify-center font-display font-black flex-shrink-0 transition ${
                          isDone ? "bg-pb-green text-white" : "bg-white text-pb-ink border-2 border-pb-sky/40"
                        }`}
                      >
                        {isDone ? <CheckCircle2 className="h-5 w-5" /> : i + 1}
                      </span>
                      <span className={`pt-1.5 leading-relaxed ${isDone ? "line-through text-pb-soft" : "text-pb-ink"}`}>
                        {s}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ol>
          </div>

          {/* Kid task + tips */}
          <div className="mt-6 grid sm:grid-cols-2 gap-5">
            <Callout
              testid="card-kid-task"
              icon={Baby}
              label="Kid's job"
              bg="#FFE3D8"
              color="#F26A34"
              text={recipe.kid_task}
            />
            <Callout
              testid="card-family-tip"
              icon={Heart}
              label="Family tip"
              bg="#D6F4F8"
              color="#4DD0E1"
              text={recipe.family_tip}
            />
          </div>

          <Callout
            testid="card-plating"
            icon={Sparkles}
            label="Plate the adventure"
            bg="#FFF6D6"
            color="#FFD54F"
            text={recipe.plating_idea}
            wide
          />
        </div>
      </section>

      {/* CTA */}
      <section className="px-5 pb-20">
        <div className="max-w-5xl mx-auto rounded-[2.5rem] bg-pb-ink text-white p-10 sm:p-14 grid md:grid-cols-3 gap-6 items-center">
          <div className="md:col-span-2">
            <p className="text-xs font-extrabold uppercase tracking-widest text-pb-sun">Ready to plate?</p>
            <h3 className="font-display font-black text-3xl md:text-4xl mt-2 leading-tight">
              Grab {buddy?.name || "your Buddy"}'s mat and make it a ritual.
            </h3>
          </div>
          <Link
            to="/products/starter-kit"
            data-testid="btn-cta-shop"
            className="inline-flex items-center justify-center rounded-full bg-pb-coral text-white font-extrabold px-7 py-4 hover:scale-105 transition shadow-[0_12px_28px_rgba(242,106,52,0.4)]"
          >
            Shop the Starter Kit
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function Meta({ icon: Icon, label }) {
  return (
    <span className="inline-flex items-center gap-2 bg-white rounded-full px-4 py-2 text-sm font-bold text-pb-ink shadow-sm border border-pb-sky/30">
      <Icon className="h-4 w-4 text-pb-coral" />
      {label}
    </span>
  );
}

function Callout({ icon: Icon, label, text, bg, color, wide, testid }) {
  if (!text) return null;
  return (
    <div
      data-testid={testid}
      className={`rounded-[1.75rem] p-6 border border-white ${wide ? "mt-5" : ""}`}
      style={{ background: bg }}
    >
      <div className="flex items-center gap-2">
        <span className="h-9 w-9 rounded-2xl flex items-center justify-center bg-white shadow-sm">
          <Icon className="h-4 w-4" style={{ color }} />
        </span>
        <p className="text-xs font-extrabold uppercase tracking-widest" style={{ color }}>
          {label}
        </p>
      </div>
      <p className="mt-3 text-pb-ink leading-relaxed">{text}</p>
    </div>
  );
}
